import {
  zCurrency,
  zUsdDollars,
  zUsdMicros,
} from "@redotech/money/usd-amounts";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { MarketingChannel } from "./marketing-channel";

export type BillingStats = z.infer<typeof billingStatsSchema>;
export const billingStatsSchema = z.object({
  included: z.number(),
  overage: z.number(),
  carrierFees: zUsdMicros.optional(),
  costMicros: zUsdMicros.optional(),
});

export type AutomationDetails = z.infer<typeof automationDetailsSchema>;
export const automationDetailsSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal("automation"),
  createdAt: z.coerce.date().optional(),
  stats: billingStatsSchema,
});

export type CampaignDetails = z.infer<typeof campaignDetailsSchema>;
export const campaignDetailsSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal("campaign"),
  createdAt: z.coerce.date().optional(),
  scheduledAt: z.coerce.date().optional(),
  stats: billingStatsSchema,
});

export type AutomationCampaignDetails = AutomationDetails | CampaignDetails;
export const automationCampaignDetailsSchema = z.union([
  automationDetailsSchema,
  campaignDetailsSchema,
]);

export type BillingWindowProcessingResult = z.infer<
  typeof billingWindowProcessingResultSchema
>;
export const billingWindowProcessingResultSchema = z.object({
  billingWindowId: z.string(),
  teamId: z.string(),
  channel: createEnum(MarketingChannel, { enumName: "MarketingChannel" }),
  startsAt: z.date(),
  endsAt: z.date(),
  totalIncluded: z.number(),
  totalOverage: z.number(),
  totalCarrierFees: z.number(),
  automations: z.array(automationDetailsSchema),
  campaigns: z.array(campaignDetailsSchema),
});

const overviewItemSchema = z.object({
  count: z.number(),
  costMicros: zUsdMicros,
});

export type OrderTrackingOverview = z.infer<typeof orderTrackingOverviewSchema>;
export const orderTrackingOverviewSchema = z.object({
  ordersIncluded: overviewItemSchema.optional(),
  ordersOverage: overviewItemSchema.optional(),
  shipmentsIncluded: overviewItemSchema.optional(),
  shipmentsOverage: overviewItemSchema.optional(),
  smsIncluded: overviewItemSchema.optional(),
  smsOverage: overviewItemSchema.optional(),
  smsCarrierFeesMicros: zUsdMicros.optional(),
  emailIncluded: overviewItemSchema.optional(),
  emailOverage: overviewItemSchema.optional(),
});

export type RecoverOrderDetails = z.infer<typeof recoverOrderDetailsSchema>;
export const recoverOrderDetailsSchema = z.object({
  orderId: z.string(),
  orderName: z.string(),
  totalPrice: zUsdDollars,
  attributedToRecoverAt: z.coerce.date(),
});

export type RecoverReturnClawbackDetails = z.infer<
  typeof recoverReturnClawbackDetailsSchema
>;
export const recoverReturnClawbackDetailsSchema = z.object({
  returnId: z.string(),
  returnProductId: z.string(),
  orderId: z.string(),
  orderName: z.string(),
  lineItemId: z.string(),
  quantity: z.number(),
  returnedRevenueDollars: zUsdDollars,
  revenueSharePercentage: z.number(),
  clawbackDollars: zUsdDollars,
  completedAt: z.coerce.date(),
  attributedToRecoverAt: z.coerce.date(),
});

export type RecoverCancellationClawbackDetails = z.infer<
  typeof recoverCancellationClawbackDetailsSchema
>;
export const recoverCancellationClawbackDetailsSchema = z.object({
  orderId: z.string(),
  orderName: z.string(),
  orderRevenueDollars: zUsdDollars,
  revenueSharePercentage: z.number(),
  clawbackDollars: zUsdDollars,
  cancelledAt: z.coerce.date(),
  cancelReason: z.string().nullish(),
  attributedToRecoverAt: z.coerce.date(),
});

export type RecoverOverview = z.infer<typeof recoverOverviewSchema>;
export const recoverOverviewSchema = z.object({
  recoveredRevenueDollars: zUsdDollars,
  recoveredOrdersCount: z.number(),
  revenueSharePercentage: z.number(),
  currency: zCurrency.optional(),
  smsIncluded: overviewItemSchema.optional(),
  smsOverage: overviewItemSchema.optional(),
  smsCarrierFeesMicros: zUsdMicros.optional(),
});

export type DisputeDetails = z.infer<typeof disputeDetailsSchema>;
export const disputeDetailsSchema = z.object({
  disputeId: z.string(),
  sourceOrderId: z.string().nullable(),
  amount: z.number(),
  currency: zCurrency,
  fee: zUsdDollars,
  finalizedOn: z.coerce.date(),
});

export type DisputeOverview = z.infer<typeof disputeOverviewSchema>;
export const disputeOverviewSchema = z.object({
  wonDisputeCount: z.number(),
  totalDisputeAmountDollars: zUsdDollars,
  totalFeeDollars: zUsdDollars,
  minFeeDollars: z.number(),
  feePercentage: z.number(),
});

/**
 * Flat-per-event billing breakdown (Alerts + Deflection). One line per usage
 * type, billed `count × unit price`. `costByCurrency` is our cost, not the bill.
 */
export type AlertDeflectionBillingLine = z.infer<
  typeof alertDeflectionBillingLineSchema
>;
export const alertDeflectionBillingLineSchema = z.object({
  alertType: z.string(),
  count: z.number(),
  unitPriceDollars: zUsdDollars,
  feeDollars: zUsdDollars,
  costByCurrency: z.record(z.string(), z.number()).optional(),
});

export type AlertDeflectionBillingOverview = z.infer<
  typeof alertDeflectionBillingOverviewSchema
>;
export const alertDeflectionBillingOverviewSchema = z.object({
  totalEventCount: z.number(),
  totalFeeDollars: zUsdDollars,
});
